import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { mediaUrl } from "@/lib/api";
import PackageCard from "@/components/marketing/PackageCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/services/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="bg-ivory text-ink min-h-screen pt-40 px-6 md:px-12">
        <p className="font-editorial text-4xl italic" data-testid="service-not-found">Service not found.</p>
        <Link to="/services" className="btn-outline-ink mt-8" data-testid="service-back-link">Back to services</Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="bg-ivory text-ink min-h-screen pt-40 px-6 md:px-12" data-testid="service-loading">
        <p className="label-mono opacity-60">Loading…</p>
      </main>
    );
  }

  const { category, packages, faqs, projects } = data;
  const addOns = (packages || []).flatMap((p) => p.add_ons || []);
  // dedupe add-ons by name
  const uniqAddons = Array.from(new Map(addOns.map((a) => [a.name, a])).values());

  return (
    <main data-testid={`service-page-${category.slug}`} className="bg-ivory text-ink pb-32">
      <section className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-ink">
        <motion.img
          src={mediaUrl(category.hero_image)}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-14 text-ivory">
          <p className="label-mono opacity-80">Services · {category.short_name}</p>
          <h1 className="mt-4 font-editorial text-6xl md:text-8xl lg:text-9xl leading-[0.95]">
            {category.name}
          </h1>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-4">
          <p className="label-mono opacity-60">Introduction</p>
        </div>
        <p className="col-span-12 md:col-span-8 font-editorial text-3xl md:text-4xl leading-[1.2] italic">
          {category.intro}
        </p>
      </section>

      {/* Packages */}
      <section className="px-6 md:px-12 lg:px-16 mt-24" id="packages">
        <div className="mb-12 flex items-baseline justify-between">
          <h2 className="font-editorial text-5xl md:text-6xl">Packages</h2>
          <p className="label-mono opacity-60 hidden md:block">All prices in ZAR</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} category={category} />
          ))}
        </div>
      </section>

      {/* What to Expect */}
      {category.what_to_expect?.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 mt-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="label-mono opacity-60 line-badge">What to Expect</p>
            <h3 className="mt-6 font-editorial text-4xl md:text-5xl">The Oereshaus session.</h3>
          </div>
          <ul className="col-span-12 md:col-span-8 space-y-6">
            {category.what_to_expect.map((line, i) => (
              <li key={i} className="flex gap-6 border-b border-ink/10 pb-6">
                <span className="label-mono opacity-40 shrink-0">0{i + 1}</span>
                <p className="text-lg text-ink/80">{line}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Client responsibility note */}
      {category.responsibility_note && (
        <section className="px-6 md:px-12 lg:px-16 mt-24" data-testid="responsibility-note">
          <div className="border-l border-ink/30 pl-6 max-w-3xl">
            <p className="label-mono opacity-60 mb-3">Please note</p>
            <p className="text-lg text-ink/80">{category.responsibility_note}</p>
          </div>
        </section>
      )}

      {/* Projects gallery */}
      {projects?.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 mt-32">
          <div className="mb-12 flex items-baseline justify-between">
            <h2 className="font-editorial text-5xl md:text-6xl">Recent {category.short_name}</h2>
            <Link to="/portfolio" className="hover-underline label-mono hidden md:inline-flex">
              View all portfolio →
            </Link>
          </div>
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {projects.map((p, i) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.slug}`}
                className={`col-span-12 ${i === 0 ? "md:col-span-8" : "md:col-span-4"} group block`}
                data-testid={`service-project-${p.slug}`}
              >
                <div className={`photo-frame ${i === 0 ? "aspect-[16/10]" : "aspect-[3/4]"}`}>
                  <img src={mediaUrl(p.cover_image)} alt={p.title} loading="lazy" />
                </div>
                <p className="mt-3 font-editorial italic text-2xl">{p.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Add-ons */}
      {uniqAddons.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 mt-32">
          <h2 className="font-editorial text-5xl md:text-6xl mb-10">Add-ons</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {uniqAddons.map((a, i) => (
              <li
                key={i}
                className="flex items-baseline justify-between gap-6 py-5 border-b border-ink/10"
              >
                <span className="text-lg text-ink/85">{a.name}</span>
                <span className="label-mono opacity-70 shrink-0">{a.price_text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FAQs */}
      {faqs?.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 mt-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="label-mono opacity-60 line-badge">FAQs</p>
            <h2 className="mt-6 font-editorial text-5xl md:text-6xl">Good questions.</h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="service-faqs">
              {faqs.slice(0, 10).map((f, i) => (
                <AccordionItem key={f.id || i} value={`item-${i}`} className="border-ink/15">
                  <AccordionTrigger
                    className="text-left font-editorial text-2xl md:text-3xl py-6 hover:no-underline"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink/70 text-base leading-relaxed pb-6">
                    {f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Booking CTA */}
      <section className="px-6 md:px-12 lg:px-16 mt-32">
        <div className="border-t border-ink/15 pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="label-mono opacity-60">Ready?</p>
            <h3 className="mt-3 font-editorial text-4xl md:text-5xl max-w-xl">
              Enquire about a {category.short_name.toLowerCase()} session.
            </h3>
          </div>
          <Link
            to={`/booking?category=${encodeURIComponent(category.slug)}`}
            className="btn-outline-ink"
            data-testid="service-cta-booking"
          >
            Start Enquiry
          </Link>
        </div>
      </section>
    </main>
  );
}
